import React, { Dispatch } from "react";
import cs from "classnames";

import { Button } from "../shared/Button/Button";
import { Icon } from "../shared/Icon/Icon";
import { ProductStoreArtwork } from "./ProductStoreArtwork";
import { ProductStoreCartAddOn } from "./ProductStoreCartAddOn";
import { ProductStoreCartSummary } from "./ProductStoreCartSummary";
import { QuantityStepper } from "./QuantityStepper";
import {
  CartAction,
  CartState,
  CartSummary,
  getAddOnQuantity,
  getRequiredBy,
} from "./productStoreCart";
import { PurchaseOffer } from "./productStorePurchase";

interface Props {
  offer: PurchaseOffer;
  cart: CartState;
  summary: CartSummary;
  dispatch: Dispatch<CartAction>;
  onContinue: () => void;
}

/** The order dialog's first step: quantities, add-ons and the summary. */
export const ProductStoreCartView = ({
  offer,
  cart,
  summary,
  dispatch,
  onContinue,
}: Props) => {
  const { product, quantity, addOns, blockedReason } = offer;
  const mainLabel = quantity.mode === "selectable" ? quantity.label : undefined;

  return (
    <>
      <div className="ProductStoreCartDialog__body">
        <div className="ProductStoreCartDialog__item">
          <div
            className={cs(
              "ProductStoreCartDialog__media",
              `ProductStoreCartDialog__media--${product.artwork}`
            )}
          >
            <ProductStoreArtwork
              artwork={product.artwork}
              alt={product.artworkAlt}
            />
          </div>
          <div className="ProductStoreCartDialog__itemText">
            <h3>{product.title}</h3>
            <p>{product.description}</p>
          </div>
          {mainLabel && (
            <div className="ProductStoreCartDialog__quantity">
              <span>{mainLabel}</span>
              <QuantityStepper
                label={product.title}
                value={cart.quantity}
                onChange={(value) =>
                  dispatch({ type: "setQuantity", quantity: value })
                }
              />
            </div>
          )}
          <p className="ProductStoreCartDialog__unitPrice">
            <strong>{product.unitPrice}</strong>
            <span>{product.unitLabel}</span>
          </p>
        </div>

        {addOns.length > 0 && (
          <section aria-labelledby="product-store-cart-add-ons">
            <div className="ProductStoreCartDialog__sectionHeader">
              <h3 id="product-store-cart-add-ons">Recommended add-ons</h3>
              <span>Enhance your setup with these popular add-ons.</span>
            </div>
            <div className="ProductStoreCartDialog__addOns">
              {addOns.map((addOn) => (
                <ProductStoreCartAddOn
                  key={addOn.id}
                  addOn={addOn}
                  selected={addOn.id in cart.addOns}
                  quantity={getAddOnQuantity(addOn, cart)}
                  mainLabel={mainLabel}
                  lockedBy={getRequiredBy(addOn.id, cart, addOns)[0]?.title}
                  onToggle={() =>
                    dispatch({ type: "toggleAddOn", addOn, addOns })
                  }
                  onQuantityChange={(value) =>
                    dispatch({
                      type: "setAddOnQuantity",
                      id: addOn.id,
                      quantity: value,
                    })
                  }
                />
              ))}
            </div>
          </section>
        )}

        <ProductStoreCartSummary summary={summary} />
      </div>

      <div className="ProductStoreCartDialog__footer">
        {blockedReason ? (
          <p className="ProductStoreCartDialog__blocked" role="alert">
            {blockedReason}
          </p>
        ) : (
          <div className="ProductStoreCartDialog__paymentNote">
            <Icon icon="lock" />
            You will be redirected to Chargebee for secure checkout, with
            payments processed by Stripe.
          </div>
        )}
        <Button
          title={
            Object.keys(cart.addOns).length
              ? "Add selected & continue"
              : "Continue to checkout"
          }
          disabled={!!blockedReason}
          onClick={onContinue}
        />
      </div>
    </>
  );
};
